import React from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

const TermsOfService: React.FC = () => {
  return (
    <div className={`min-h-screen w-full ${gradientBg} py-8 px-4`}>
      <div className="max-w-4xl mx-auto">
        <div className={glassCard}>
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-emerald-100 mb-4">
              Conditions d'Utilisation
            </h1>
            <p className="text-emerald-200">
              Dernière mise à jour : 21 octobre 2025
            </p>
          </div>

          <div className="space-y-8 text-emerald-200">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">1. Acceptation des conditions</h2>
              <p className="leading-relaxed mb-4">
                Bienvenue sur Jupipotes ! En accédant ou en utilisant notre plateforme, vous acceptez 
                d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas ces conditions, 
                veuillez ne pas utiliser Jupipotes.
              </p>
              <p className="leading-relaxed bg-cyan-900/20 p-4 rounded-lg border border-cyan-700/30">
                ⚠️ Ces conditions constituent un accord juridiquement contraignant entre vous et Jupipotes.
              </p>
            </section>

            {/* Éligibilité */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">2. Éligibilité</h2>
              <p className="mb-3">Pour utiliser Jupipotes, vous devez :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Avoir au moins 13 ans</li>
                <li>Avoir la capacité juridique de conclure un contrat contraignant</li>
                <li>Ne pas être interdit d'utiliser nos services selon la loi applicable</li>
                <li>Respecter toutes les lois locales, régionales et nationales</li>
              </ul>
            </section>

            {/* Création de compte */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">3. Création et gestion de compte</h2>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">3.1 Inscription</h3>
              <p className="mb-4">
                Lors de la création d'un compte, vous devez fournir des informations exactes, 
                complètes et à jour. Vous êtes responsable de maintenir la confidentialité de 
                vos identifiants de connexion.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">3.2 Sécurité du compte</h3>
              <p className="mb-4">
                Vous êtes entièrement responsable de toutes les activités qui se produisent sous 
                votre compte. Notifiez-nous immédiatement toute utilisation non autorisée de votre 
                compte.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">3.3 Suspension et résiliation</h3>
              <p>
                Nous nous réservons le droit de suspendre ou de résilier votre compte à tout moment 
                si vous violez ces conditions ou si nous estimons que votre compte menace la sécurité 
                ou l'intégrité de la plateforme.
              </p>
            </section>

            {/* Utilisation de la plateforme */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">4. Utilisation acceptable</h2>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">4.1 Comportements autorisés</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Créer et partager du contenu original</li>
                <li>Interagir de manière respectueuse avec les autres utilisateurs</li>
                <li>Participer aux communautés et au DAO</li>
                <li>Utiliser les fonctionnalités conformément à leur usage prévu</li>
              </ul>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">4.2 Comportements interdits</h3>
              <p className="mb-3">Vous ne devez pas :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Publier du contenu illégal, haineux, violent ou pornographique</li>
                <li>Harceler, intimider ou menacer d'autres utilisateurs</li>
                <li>Usurper l'identité d'une autre personne ou entité</li>
                <li>Spammer ou envoyer des messages non sollicités</li>
                <li>Utiliser des bots, scrapers ou outils automatisés sans autorisation</li>
                <li>Tenter de contourner les mesures de sécurité</li>
                <li>Manipuler le système de votes ou de récompenses</li>
                <li>Violer les droits de propriété intellectuelle d'autrui</li>
                <li>Collecter des données utilisateurs sans consentement</li>
              </ul>
            </section>

            {/* Contenu utilisateur */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">5. Contenu utilisateur</h2>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">5.1 Votre contenu</h3>
              <p className="mb-4">
                Vous conservez tous les droits sur le contenu que vous publiez sur Jupipotes. 
                Cependant, en publiant du contenu, vous nous accordez une licence mondiale, 
                non exclusive, libre de redevance pour utiliser, reproduire, distribuer et 
                afficher ce contenu dans le cadre de nos services.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">5.2 Responsabilité du contenu</h3>
              <p className="mb-4">
                Vous êtes seul responsable du contenu que vous publiez. Nous ne cautionnons pas 
                et ne sommes pas responsables du contenu des utilisateurs.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">5.3 Modération</h3>
              <p>
                Nous nous réservons le droit de supprimer tout contenu qui viole ces conditions 
                ou qui est jugé inapproprié, sans préavis.
              </p>
            </section>

            {/* Propriété intellectuelle */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">6. Propriété intellectuelle</h2>
              <p className="mb-4">
                Jupipotes et tous ses contenus, fonctionnalités et fonctionnalités (y compris 
                mais sans s'y limiter, toutes les informations, logiciels, textes, affichages, 
                images, vidéos et audio) sont la propriété de Jupipotes ou de ses concédants de 
                licence et sont protégés par les lois sur le droit d'auteur, les marques et autres 
                lois sur la propriété intellectuelle.
              </p>
              <p>
                Vous ne pouvez pas copier, modifier, distribuer, vendre ou louer une partie de 
                nos services sans notre autorisation écrite préalable.
              </p>
            </section>

            {/* Tokens et récompenses */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">7. Tokens JUP et récompenses</h2>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">7.1 Nature des tokens</h3>
              <p className="mb-4">
                Les tokens JUP sont des tokens utilitaires utilisés pour la gouvernance, les 
                récompenses et l'accès à certaines fonctionnalités. Ils ne constituent pas un 
                investissement et n'ont aucune valeur monétaire garantie.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">7.2 Distribution des récompenses</h3>
              <p className="mb-4">
                Les récompenses en tokens JUP sont distribuées à la discrétion de Jupipotes et 
                selon les règles du DAO. Nous nous réservons le droit de modifier les mécanismes 
                de récompense à tout moment.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">7.3 Interdictions</h3>
              <p className="bg-red-900/20 p-4 rounded-lg border border-red-700/30">
                ⚠️ Il est strictement interdit de manipuler le système de récompenses par des 
                moyens frauduleux (faux comptes, bots, etc.). Toute violation entraînera la 
                suspension immédiate du compte et la confiscation des tokens.
              </p>
            </section>

            {/* DAO et gouvernance */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">8. DAO et gouvernance</h2>
              <p className="mb-4">
                En participant au DAO, vous acceptez de vous conformer aux règles de gouvernance 
                établies par la communauté. Les votes et décisions du DAO sont contraignants et 
                seront mis en œuvre conformément aux mécanismes établis.
              </p>
              <p>
                Vous reconnaissez que les votes et transactions blockchain sont permanents et 
                ne peuvent pas être annulés.
              </p>
            </section>

            {/* Limitation de responsabilité */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">9. Limitation de responsabilité</h2>
              <p className="mb-4">
                JUPIPOTES EST FOURNI "TEL QUEL" ET "TEL QUE DISPONIBLE" SANS GARANTIE D'AUCUNE 
                SORTE. NOUS NE GARANTISSONS PAS QUE LE SERVICE SERA ININTERROMPU, SÉCURISÉ OU 
                EXEMPT D'ERREURS.
              </p>
              <p className="mb-4">
                DANS LA MESURE MAXIMALE PERMISE PAR LA LOI, JUPIPOTES NE SERA PAS RESPONSABLE 
                DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS, NI DE 
                LA PERTE DE PROFITS, REVENUS, DONNÉES OU UTILISATION.
              </p>
              <p className="bg-cyan-900/20 p-4 rounded-lg border border-cyan-700/30">
                ℹ️ Certaines juridictions ne permettent pas l'exclusion de certaines garanties 
                ou limitations de responsabilité, ces limitations peuvent donc ne pas s'appliquer 
                à vous.
              </p>
            </section>

            {/* Indemnisation */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">10. Indemnisation</h2>
              <p className="leading-relaxed">
                Vous acceptez d'indemniser, de défendre et de dégager de toute responsabilité 
                Jupipotes, ses dirigeants, administrateurs, employés et agents contre toute 
                réclamation, dommage, obligation, perte, responsabilité, coût ou dette, et 
                dépense résultant de : (a) votre utilisation et accès au service ; (b) votre 
                violation de ces conditions ; (c) votre violation de tout droit de tiers, 
                y compris tout droit d'auteur, propriété ou vie privée.
              </p>
            </section>

            {/* Droit applicable */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">11. Droit applicable et juridiction</h2>
              <p className="leading-relaxed mb-4">
                Ces conditions sont régies par les lois applicables, sans égard aux conflits 
                de dispositions légales. Tout litige découlant de ou lié à ces conditions sera 
                résolu par arbitrage conformément aux règles en vigueur.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">12. Modifications des conditions</h2>
              <p className="leading-relaxed">
                Nous nous réservons le droit de modifier ces conditions à tout moment. Les 
                modifications prennent effet immédiatement après leur publication. Votre 
                utilisation continue de Jupipotes après la publication des modifications 
                constitue votre acceptation des nouvelles conditions.
              </p>
            </section>

            {/* Dispositions générales */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">13. Dispositions générales</h2>
              
              <h3 className="text-xl font-semibold text-emerald-100 mb-3">13.1 Intégralité de l'accord</h3>
              <p className="mb-4">
                Ces conditions constituent l'intégralité de l'accord entre vous et Jupipotes.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">13.2 Divisibilité</h3>
              <p className="mb-4">
                Si une disposition est jugée invalide, les autres dispositions resteront en vigueur.
              </p>

              <h3 className="text-xl font-semibold text-emerald-100 mb-3">13.3 Renonciation</h3>
              <p>
                L'absence d'exercice d'un droit ne constitue pas une renonciation à ce droit.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-300 mb-4">14. Nous contacter</h2>
              <p className="leading-relaxed mb-4">
                Pour toute question concernant ces conditions d'utilisation, contactez-nous :
              </p>
              <div className="bg-cyan-900/20 p-4 rounded-lg border border-cyan-700/30">
                <p className="mb-2"><strong>Email :</strong> legal@jupipotes.fr</p>
                <p><strong>Support :</strong> <Link to="/support" className="text-cyan-300 hover:text-cyan-200 underline">Page de contact</Link></p>
              </div>
            </section>
          </div>

          {/* Navigation */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
            <Link
              to="/privacy-policy"
              className="text-cyan-300 hover:text-cyan-200 transition-colors flex items-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Politique de confidentialité
            </Link>
            <Link
              to="/JUPResources"
              className="text-emerald-300 hover:text-emerald-200 transition-colors flex items-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour aux ressources
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
